import React from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { getWithTokenAPI } from '../../util/Api';
import { FiHeart, FiSend, FiMessageSquare } from 'react-icons/fi';


function UserProfile() {
  const { id } = useParams();
  const location = useLocation();
  const token = localStorage.getItem('matrimony_token');

  // Extract ID from pathname as fallback
  const extractIdFromPath = (pathname) => {
    const match = pathname.match(/\/admin\/user-profile\/([^\/]+)/);
    return match ? match[1] : null;
  };

  const finalId = id || extractIdFromPath(location.pathname);

  // State management
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [planData, setPlanData] = useState(null);
  const [activeTab, setActiveTab] = useState('basic');

  // Interactive features states
  const [interestData, setInterestData] = useState({ received: [], sent: [], receivedCount: 0, sentCount: 0 });
  const [messageData, setMessageData] = useState({ messages: [], count: 0 });
  const [showInterestReceivedModal, setShowInterestReceivedModal] = useState(false);
  const [showInterestSentModal, setShowInterestSentModal] = useState(false);
  const [showMessagesModal, setShowMessagesModal] = useState(false);

  console.log(userData, "userData====")
  // Fetch user details
  useEffect(() => {
    const fetchUserDetails = async () => {
      try {
        setLoading(true);
        setError(null);

        const token = localStorage.getItem('matrimony_token');
        if (!token) {
          throw new Error("Please login again");
        }

        const response = await getWithTokenAPI(token, `/profile/get_all/${finalId}`);

        if (response.data) {
          setUserData(response.data);
        } else {
          throw new Error(response.message || "Failed to load user details");
        }

      } catch (error) {
        console.error('Error fetching user details:', error);
        setError(error.message || "Failed to load user details");
      } finally {
        setLoading(false);
      }
    };

    if (finalId) {
      fetchUserDetails();
    }
  }, [finalId]);

  // Fetch plan details
  const getPlanDetail = async () => {
    try {
      const response = await getWithTokenAPI(token, `/userSubscription/get_by_id?user_id=${finalId}`);
      if (response.data) {
        setPlanData(response.data);
      }
    } catch (error) {
      console.error('Error fetching plan details:', error);
    }
  };

  useEffect(() => {
    getPlanDetail();
  }, [finalId]);

  // Fetch additional data
  useEffect(() => {
    const fetchAdditionalData = async () => {
      try {
        const token = localStorage.getItem('matrimony_token');
        if (!token) return;

        const [interestResponse, sentInterestResponse, messagesResponse] = await Promise.all([
          getWithTokenAPI(token, `/interest/get_interest?user_id=${finalId}`),
          getWithTokenAPI(token, `/interest/get_send_interest?user_id=${finalId}`),
          getWithTokenAPI(token, `/message/inbox?user_id=${finalId}`)
        ]);

        setInterestData({
          received: interestResponse.data?.interested_user_details || [],
          sent: sentInterestResponse.data?.receivers || [],
          receivedCount: interestResponse.data?.total_interests || 0,
          sentCount: sentInterestResponse.data?.total_sent || 0
        });

        setMessageData({
          messages: messagesResponse.data?.data || [],
          count: messagesResponse.data?.total || 0
        });

      } catch (error) {
        console.error('Error fetching additional data:', error);
      }
    };

    if (finalId && token) {
      fetchAdditionalData();
    }
  }, [finalId]);

  // Helper functions
  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (imagePath.startsWith('http')) return imagePath;
    if (imagePath.startsWith(':\\')) return null;
    return `${process.env.REACT_APP_API_BASE_URL || ''}${imagePath}`;
  };

  const zodiacCodes = {
    'pu': 'Pudhan', 'cha': 'Chandran', 'chk': 'Sukran', 'sun': 'Soonyan',
    'viy': 'Viyalan', 'sev': 'Sevai', 'kee': 'Kedhu', 'lac': 'Lakanm',
    'ragu': 'Raagu', 'sat': 'Sani'
  };

  const getZodiacName = (code) => zodiacCodes[code] || code;

  const getZodiacValue = (houseType, houseNumber) => {
    const houseData = userData?.zodiac?.[houseType]?.[`House_${houseNumber}`];
    if (houseData && houseData.length > 0) {
      return houseData.map(code => getZodiacName(code)).join(', ');
    }
    return '';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric', month: 'long', day: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'active': { color: 'green', text: 'Active' },
      'inactive': { color: 'red', text: 'Inactive' },
      'expired': { color: 'gray', text: 'Expired' },
      'cancelled': { color: 'orange', text: 'Cancelled' }
    };
    const config = statusConfig[status?.toLowerCase()] || { color: 'gray', text: status || 'Unknown' };
    return (
      <span className={`ml-1 px-2 py-1 rounded-full text-xs bg-${config.color}-100 text-${config.color}-800`}>
        {config.text}
      </span>
    );
  };

  const BasicInfoTab = () => (
    <div className="profile-section p-6">
      <h2 className="text-xl font-semibold mb-4 text-gray-800">Basic Information</h2>
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(3, 1fr)",
        gap: "16px", // gap-4 = 1rem = 16px
      }}>
        <div className="detail-item">
          <strong className="text-gray-700">Name:</strong> {userData?.basicProfile?.name || 'N/A'}
        </div>
        <div className="detail-item">
          <strong className="text-gray-700">Mobile Number:</strong> {userData?.basicProfile?.mobile_number || 'N/A'}
        </div>
        <div className="detail-item">
          <strong className="text-gray-700">Gender:</strong> {userData?.basicProfile?.gender || 'N/A'}
        </div>
        <div className="detail-item">
          <strong className="text-gray-700">Date of Birth:</strong> {userData?.basicProfile?.dob || 'N/A'}
        </div>
        <div className="detail-item">
          <strong className="text-gray-700">Age:</strong> {userData?.basicProfile?.age || 'N/A'}
        </div>
        <div className="detail-item">
          <strong className="text-gray-700">City:</strong> {userData?.basicProfile?.city || 'N/A'}
        </div>
        <div className="detail-item">
          <strong className="text-gray-700">District:</strong> {userData?.basicProfile?.district || 'N/A'}
        </div>

       

        <div className="detail-item">
          <strong className="text-gray-700">Marital Status:</strong> {userData?.basicProfile?.maritalStatus || 'N/A'}
        </div>
        <div className="detail-item">
          <strong className="text-gray-700">Status:</strong>
          <span className={`ml-1 px-2 py-1 rounded-full text-xs ${userData?.basicProfile?.status === 'Active'
            ? 'bg-green-100 text-green-800'
            : 'bg-red-100 text-red-800'
            }`}>
            {userData?.basicProfile?.status || 'N/A'}
          </span>
        </div>
         <div className="detail-item">
          <strong className="text-gray-700">Registration Number:</strong> {userData?.basicProfile?.registrationNumber || 'N/A'}
        </div>
        
        <div className="detail-item">
          <strong className="text-gray-700">Verified:</strong>
          <span className={`ml-1 px-2 py-1 rounded-full text-xs ${userData?.basicProfile?.isVerified
            ? 'bg-blue-100 text-blue-800'
            : 'bg-yellow-100 text-yellow-800'
            }`}>
            {userData?.basicProfile?.isVerified ? 'Yes' : 'No'}
          </span>
        </div>
        <div className="detail-item">
          <strong className="text-gray-700">Profile Created:</strong>
          <span className={`ml-1 px-2 py-1 rounded-full text-xs ${userData?.basicProfile?.created_by_admin
            ? 'bg-purple-100 text-purple-800'
            : 'bg-gray-100 text-gray-800'
            }`}>
            {userData?.basicProfile?.created_by_admin ? 'By Admin' : 'By User'}
          </span>
        </div>
      </div>

      {planData && (
        <div className="mt-8">
          <h2 className="text-xl font-semibold mb-4 text-gray-800">Subscription Plan Details</h2>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: "16px", // gap-4 = 1rem = 16px
            }}
          >
            <div className="detail-item">
              <strong className="text-gray-700">Plan Name:</strong>
              <span className="ml-1 font-semibold text-blue-600">{planData.plan_name || 'N/A'}</span>
            </div>
            <div className="detail-item">
              <strong className="text-gray-700">Plan Price:</strong>
              <span className="ml-1 font-semibold text-green-600">
                ₹{planData.plan_price ? planData.plan_price.toLocaleString('en-IN') : 'N/A'}
              </span>
            </div>
            <div className="detail-item">
              <strong className="text-gray-700">Plan Duration:</strong>
              <span className="ml-1 font-semibold text-purple-600">
                {planData.plan_duration_days || 'N/A'} days
              </span>
            </div>
            <div className="detail-item">
              <strong className="text-gray-700">Status:</strong>
              {getStatusBadge(planData.status)}
            </div>
            <div className="detail-item">
              <strong className="text-gray-700">Start Date:</strong>
              <span className="ml-1 text-gray-600">{formatDate(planData.start_date)}</span>
            </div>
            <div className="detail-item">
              <strong className="text-gray-700">End Date:</strong>
              <span className="ml-1 text-gray-600">{formatDate(planData.end_date)}</span>
            </div>
            <div className="detail-item">
              <strong className="text-gray-700">Auto Renew:</strong>
              <span className={`ml-1 px-2 py-1 rounded-full text-xs ${planData.auto_renew
                ? 'bg-green-100 text-green-800'
                : 'bg-gray-100 text-gray-800'
                }`}>
                {planData.auto_renew ? 'Yes' : 'No'}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const AstrologyDetailsTab = () => (
    <div className="profile-section p-6">
      <h2 className="text-xl font-semibold mb-4 text-gray-800">Astrology Details</h2>
      {userData?.professionalProfile?.astrology ? (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: "16px", // gap-4 = 1rem = 16px
          }}
        >
          <div className="detail-item">
            <strong className="text-gray-700">Rasi:</strong> {userData.professionalProfile.astrology.rasi || 'N/A'}
          </div>
          <div className="detail-item">
            <strong className="text-gray-700">Natchathiram:</strong> {userData.professionalProfile.astrology.natchathiram || 'N/A'}
          </div>
          <div className="detail-item">
            <strong className="text-gray-700">Gothram:</strong> {userData.professionalProfile.astrology.gothram || 'N/A'}
          </div>
          <div className="detail-item">
            <strong className="text-gray-700">Laknam:</strong> {userData.professionalProfile.astrology.laknam || 'N/A'}
          </div>
          <div className="detail-item">
            <strong className="text-gray-700">Dosham:</strong> {userData.professionalProfile.astrology.dosham || 'N/A'}
          </div>
          <div className="detail-item">
            <strong className="text-gray-700">Kuja Dosham:</strong> {userData.professionalProfile.astrology.kujaDosham || 'N/A'}
          </div>
        </div>
      ) : (
        <p className="text-gray-500 text-center py-8">No astrology details available</p>
      )}
    </div>
  );

  const PartnerPreferencesTab = () => (
    <div className="profile-section p-6">
      <h2 className="text-xl font-semibold mb-4 text-gray-800">Partner Preferences</h2>
      {userData?.preference ? (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: "16px", // gap-4 = 1rem = 16px
          }}
        >
          <div className="detail-item">
            <strong className="text-gray-700">Age Range:</strong> {userData.preference.min_age || 'N/A'} - {userData.preference.max_age || 'N/A'} years
          </div>
          <div className="detail-item">
            <strong className="text-gray-700">Height Range:</strong> {userData.preference.height_range?.min || 'N/A'} - {userData.preference.height_range?.max || 'N/A'} cm
          </div>
          <div className="detail-item">
            <strong className="text-gray-700">Mother Tongue:</strong> {userData.preference.mother_tongue?.join(', ') || 'N/A'}
          </div>
          <div className="detail-item">
            <strong className="text-gray-700">Religion:</strong> {userData.preference.religion?.join(', ') || 'N/A'}
          </div>
          <div className="detail-item">
            <strong className="text-gray-700">Caste:</strong> {userData.preference.caste?.join(', ') || 'N/A'}
          </div>
          <div className="detail-item">
            <strong className="text-gray-700">Sub Caste:</strong> {userData.preference.sub_caste?.join(', ') || 'N/A'}
          </div>
          <div className="detail-item">
            <strong className="text-gray-700">Education Level:</strong> {userData.preference.education_level?.join(', ') || 'N/A'}
          </div>
          <div className="detail-item">
            <strong className="text-gray-700">City:</strong> {userData.preference.city?.join(', ') || 'N/A'}
          </div>
          <div className="detail-item">
            <strong className="text-gray-700">Family Status:</strong> {userData.preference.family_status?.join(', ') || 'N/A'}
          </div>
          <div className="detail-item">
            <strong className="text-gray-700">Family Type:</strong> {userData.preference.family_type?.join(', ') || 'N/A'}
          </div>
          <div className="detail-item">
            <strong className="text-gray-700">Family Values:</strong> {userData.preference.family_values?.join(', ') || 'N/A'}
          </div>
          {userData.preference.about_partner && (
            <div className="detail-item col-span-full">
              <strong className="text-gray-700">About Partner:</strong>
              <p className="mt-2 p-3 bg-gray-50 rounded-lg border">{userData.preference.about_partner}</p>
            </div>
          )}
        </div>
      ) : (
        <p className="text-gray-500 text-center py-8">No partner preferences available</p>
      )}
    </div>
  );

  const ZodiacDetailsTab = ({ getZodiacValue }) => {

    // 🟦 Box UI
    const Box = ({ label }) => (
      <div
        style={{
          width: "85px",
          height: "85px",
          backgroundColor: "#F5F5F5",
          borderRadius: "14px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          fontSize: "14px",
          color: "#4B5563",
          border: "1px solid #E5E7EB",
          textAlign: "center",
          padding: "5px",
        }}
      >
        {label || ""}
      </div>
    );

    // 🟩 HOUSE ORDER (same as React-Native)
    const houseGrid = [
      [1, 2, 3, 4],
      [12, null, null, 5],
      [11, null, null, 6],
      [10, 9, 8, 7],
    ];

    // 🟪 GRID RENDERING
    const renderGrid = (title, type) => (
      <div style={{ marginBottom: "60px", marginTop: "30px" }}>
        {/* Title */}
        <h3
          style={{
            textAlign: "center",
            fontSize: "20px",
            fontWeight: "700",
            color: "#1F2937",
            marginBottom: "30px",
          }}
        >
          {title}
        </h3>

        {/* Astrology Layout Grid */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: "20px",
            justifyItems: "center",
            maxWidth: "450px",
            margin: "0 auto",
          }}
        >
          {houseGrid.flat().map((houseNum, index) =>
            houseNum ? (
              <Box
                key={index}
                label={getZodiacValue(type, houseNum) || ""}
              />
            ) : (
              <div key={index} style={{ width: 85, height: 85 }} />
            )
          )}
        </div>
      </div>
    );
    return (
      <div style={{ padding: "20px" }}>
        <div
          style={{
            display: "flex",
            gap: "40px",
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}
        >
          {renderGrid("ராசி", "rasi")}
          {renderGrid("நவம்சம்", "navamsa")}
        </div>
      </div>
    );

  };


  const PhotosTab = () => {
    const imageUrl = userData?.basicProfile?.profileImage;
    const gallery = userData?.professionalProfile?.gallery || [];

    return (
      <div style={{ padding: "20px" }}>
        <h2
          style={{
            fontSize: "20px",
            fontWeight: "600",
            marginBottom: "20px",
          }}
        >
          Profile Image
        </h2>

        {/* MAIN PROFILE IMAGE */}
        {imageUrl ? (
          <img
            src={imageUrl}
            alt="Profile"
            style={{
              width: "220px",
              height: "220px",
              objectFit: "cover",
              borderRadius: "14px",
              border: "3px solid #eee",
              boxShadow: "0px 3px 10px rgba(0,0,0,0.1)",
              marginBottom: "20px",
            }}
          />
        ) : (
          <p style={{ color: "#666" }}>No Image Found</p>
        )}

        {/* GALLERY SECTION */}
        {gallery.length > 0 && (
          <>
            <h3
              style={{
                fontSize: "18px",
                fontWeight: "600",
                marginTop: "25px",
                marginBottom: "15px",
              }}
            >
              Gallery Images
            </h3>

            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "15px",
              }}
            >
              {gallery.map((img, idx) => (
                <img
                  key={idx}
                  src={img}
                  alt={`gallery-${idx}`}
                  style={{
                    width: "150px",
                    height: "150px",
                    objectFit: "cover",
                    borderRadius: "12px",
                    border: "2px solid #eee",
                    boxShadow: "0px 2px 8px rgba(0,0,0,0.1)",
                  }}
                />
              ))}
            </div>
          </>
        )}

        {gallery.length === 0 && (
          <p style={{ color: "#666" }}>No Gallery Images</p>
        )}
      </div>
    );
  };

  const InterestReceivedDrawer = ({ onClose, interestData }) => (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9998,
        overflow: "hidden",
      }}>
      <div
        onClick={onClose}
        style={{
          position: "absolute",
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,          
          backgroundColor: "rgba(0, 0, 0, 0.5)",
        }}
      ></div>
      <div className="absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl">
        <div className="flex flex-col h-full">
          <div
            style={{
              flexShrink: 0,
              padding: "24px", 
              color: "white",
              backgroundImage: "linear-gradient(to bottom right, #16a34a, #15803d)",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <h3
                style={{
                  fontSize: "20px",      
                  fontWeight: "600",     
                  color: "white",
                }}
              >
                Interest Received ({interestData.receivedCount})
              </h3>
              <button
                onClick={onClose}
                style={{
                  width: "32px",            
                  height: "32px",           
                  borderRadius: "9999px",     
                  backgroundColor: "#15803d", 
                  color: "white",
                  fontWeight: "700",
                  border: "1px solid #22c55e", 
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "0.2s",
                }}
              >
                ✕
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-6">
            <div className="space-y-3">
              {interestData.received.length > 0 ? (
                interestData.received.map((user, index) => (
                  <div key={index} className="flex items-center justify-between p-4 border border-green-200 rounded-lg bg-green-50 hover:bg-green-100 transition-colors">
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-green-200 rounded-full flex items-center justify-center font-semibold text-green-800">
                        {user.name?.charAt(0) || 'U'}
                      </div>
                      <div>
                        <p className="font-semibold text-green-900">{user.name || 'Unknown User'}</p>
                        <p className="text-sm text-green-700">{user.age} years • {user.city}</p>
                      </div>
                    </div>
                    <span className="text-sm text-green-600 bg-green-100 px-3 py-1 rounded">{user.mobile_number}</span>
                  </div>
                ))
              ) : (
                <p className="text-center text-green-600 py-6 bg-green-50 rounded-lg border border-green-200">No interests received</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const InterestSentDrawer = ({ onClose, interestData }) => (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9998,
        overflow: "hidden",
      }}
    >
      <div
        onClick={onClose}
        style={{
          position: "absolute",
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,           
          backgroundColor: "rgba(0, 0, 0, 0.5)", 
        }}
      ></div>
      <div className="absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl">
        <div className="flex flex-col h-full">
          <div
            style={{
              flexShrink: 0,
              padding: "24px",                 
              color: "white",
              backgroundImage: "linear-gradient(to bottom right, #2563eb, #1d4ed8)", 
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <h3
                style={{
                  fontSize: "20px",      
                  fontWeight: "600",     
                  color: "white",
                }}
              >
                Interest Sent ({interestData.sentCount})
              </h3>

              <button
                onClick={onClose}
                style={{
                  width: "32px",          
                  height: "32px",          
                  borderRadius: "9999px",  
                  backgroundColor: "#1d4ed8", 
                  color: "white",
                  fontWeight: "700",
                  border: "1px solid #3b82f6",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "0.2s",
                }}
              >
                ✕
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-6">
            <div className="space-y-3">
              {interestData.sent.length > 0 ? (
                interestData.sent.map((user, index) => (
                  <div key={index} className="flex items-center justify-between p-4 border border-blue-200 rounded-lg bg-blue-50 hover:bg-blue-100 transition-colors">
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-blue-200 rounded-full flex items-center justify-center font-semibold text-blue-800">
                        {user.name?.charAt(0) || 'U'}
                      </div>
                      <div>
                        <p className="font-semibold text-blue-900">{user.name || 'Unknown User'}</p>
                        <p className="text-sm text-blue-700">{user.age} years • {user.city}</p>
                      </div>
                    </div>
                    <span className="text-sm text-blue-600 bg-blue-100 px-3 py-1 rounded">{user.mobile_number}</span>
                  </div>
                ))
              ) : (
                <p className="text-center text-blue-600 py-6 bg-blue-50 rounded-lg border border-blue-200">No interests sent</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );



  const MessagesDrawer = ({ onClose, messageData }) => (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9998,
        overflow: "hidden",
      }}
    >

      {/* Background Overlay */}
      <div
        onClick={onClose}
        style={{
          position: "absolute",
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,           // inset-0
          backgroundColor: "rgba(0, 0, 0, 0.5)", // bg-black bg-opacity-50
        }}
      ></div>
      {/* Drawer Panel */}
      <div className="absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl flex flex-col z-[9999]">

        {/* Header */}
        <div className="bg-gradient-to-br from-purple-600 to-purple-700 text-white">
          <div
            style={{
              padding: "24px",
              background: "linear-gradient(to bottom right, #9333ea, #7e22ce)",
              color: "white",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <h3
                style={{
                  fontSize: "20px",
                  fontWeight: 600,
                }}
              >
                Messages ({messageData.count})
              </h3>

              <button
                onClick={onClose}
                style={{
                  background: "#7e22ce",
                  border: "1px solid #a855f7",
                  width: "32px",
                  height: "32px",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "white",
                  fontWeight: "bold",
                  cursor: "pointer",
                  transition: "0.2s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "#6b21a8";
                  e.currentTarget.style.color = "#e9d5ff";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "#7e22ce";
                  e.currentTarget.style.color = "white";
                }}
              >
                ✕
              </button>
            </div>
          </div>

        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {messageData?.messages?.length > 0 ? (
            messageData.messages.map((message, index) => (
              <div
                key={index}
                className="flex justify-between p-4 border border-purple-200 rounded-lg bg-purple-50 hover:bg-purple-100 transition-colors mb-3"
              >
                {/* Left Side */}
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-purple-200 rounded-full flex items-center justify-center font-semibold text-purple-800">
                    {message.sender_id?.name?.charAt(0) || "U"}
                  </div>

                  <div>
                    <p className="font-semibold text-purple-900">
                      {message.sender_id?.name || "Unknown User"}
                    </p>
                    <p className="text-sm text-purple-700">
                      {new Date(message.createdAt).toLocaleDateString()} •{" "}
                      {message.sender_id?.mobile_number}
                    </p>
                  </div>
                </div>

                {/* Right Side */}
                <div className="text-right">
                  <p className="text-sm text-purple-700 mb-2 max-w-[160px] truncate">
                    {message.content}
                  </p>

                  {!message.is_read && (
                    <span className="text-xs text-purple-700 bg-purple-100 px-2 py-1 rounded-full">
                      Unread
                    </span>
                  )}
                </div>
              </div>
            ))
          ) : (
            <p className="text-center text-purple-700 py-6 bg-purple-50 border border-purple-200 rounded-lg">
              No messages
            </p>
          )}
        </div>
      </div>
    </div>
  );



  // const getZodiacValue = (type, houseNum) => {
  //   return (profileDetails?.[type]?.[`House_${houseNum}`] || "");
  // };

  // Conditional returns for loading, error, no data
  if (loading) return (
    <div className="space-y-6 p-4">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div className="skeleton h-8 w-48"></div>
        <div className="skeleton h-10 w-32 rounded-lg"></div>
      </div>

      {/* 3 stats cards */}
      <div className="flex flex-wrap gap-4 justify-between mb-6">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="flex-1 min-w-[200px] bg-white border border-gray-100 rounded-xl p-4 shadow-sm flex items-start gap-4">
            <div className="skeleton w-12 h-12 rounded-xl flex-shrink-0"></div>
            <div className="flex-1 space-y-2">
              <div className="skeleton h-3 w-20"></div>
              <div className="skeleton h-6 w-12"></div>
              <div className="skeleton h-2 w-24"></div>
            </div>
          </div>
        ))}
      </div>

      {/* Tabs bar */}
      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        <div className="border-b flex justify-evenly p-3">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="skeleton h-4 w-24 my-2"></div>
          ))}
        </div>
        
        {/* Large Content Area Skeleton */}
        <div className="p-6 space-y-6">
          <div className="flex items-center space-x-6 border-b pb-6">
            <div className="skeleton w-24 h-24 rounded-full flex-shrink-0"></div>
            <div className="flex-1 space-y-3">
              <div className="skeleton h-6 w-1/4"></div>
              <div className="skeleton h-4 w-1/3"></div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="space-y-2">
                <div className="skeleton h-3 w-1/4"></div>
                <div className="skeleton h-5 w-3/4"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  if (error) return (
    <div className="flex items-center justify-center h-64">
      <div className="text-center">
        <div className="text-red-600 text-xl font-semibold">Error</div>
        <p className="text-gray-700 mt-2">{error}</p>
        <p className="text-sm text-gray-500 mt-2">User ID: {finalId}</p>
        <button
          onClick={() => window.history.back()}
          className="mt-4 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded"
        >
          Go Back
        </button>
      </div>
    </div>
  );

  if (!userData) return (
    <div className="flex items-center justify-center h-64">
      <div className="text-center">
        <div className="text-gray-600 text-xl">User not found</div>
        <p className="text-sm text-gray-500 mt-2">User ID: {finalId}</p>
        <button
          onClick={() => window.history.back()}
          className="mt-4 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded"
        >
          Go Back
        </button>
      </div>
    </div>
  );

  return (
    <div className="user-profile-page">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">User Profile Details</h1>
        <button
          onClick={() => window.history.back()}
        className="px-6 py-2 text-white rounded-lg disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-300 flex items-center gap-2 cursor-pointer"
  style={{
    background:
      "linear-gradient(to right bottom, rgb(220, 38, 38), rgb(243, 67, 67))",
  }} >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to List
        </button>
      </div>

      <div style={{ marginBottom: "24px" }}>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "16px",
            justifyContent: "space-between",
          }}
        >
          {/* Interest Received */}
          <button
            onClick={() => setShowInterestReceivedModal(true)}
            style={{
              flex: 1,
              minWidth: "200px",
              background: "white",
              padding: "16px",
              borderRadius: "14px",
              border: "1px solid #f0f0f0",
              boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
              display: "flex",
              alignItems: "flex-start",
              gap: "16px",
              cursor: "pointer",
              transition: "all 0.2s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-4px)";
              e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.15)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "0 1px 4px rgba(0,0,0,0.08)";
            }}
          >
            <div
              style={{
                minWidth: "48px",
                height: "48px",
                borderRadius: "12px",
                background: "linear-gradient(135deg, #22c55e, #15803d)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "white",
                boxShadow: "inset 0 0 4px rgba(0,0,0,0.2)",
              }}
            >
              <FiHeart size={24} />
            </div>

            <div style={{ textAlign: "left" }}>
              <p style={{ fontSize: "12px", fontWeight: 600, color: "#6b7280" }}>
                Interest Received
              </p>
              <p
                style={{
                  fontSize: "26px",
                  fontWeight: 800,
                  marginTop: "4px",
                  color: "#111827",
                }}
              >
                {interestData.receivedCount}
              </p>
              <p style={{ fontSize: "10px", marginTop: "6px", color: "#9ca3af" }}>
                Click to view details
              </p>
            </div>
          </button>

          {/* Interest Sent */}
          <button
            onClick={() => setShowInterestSentModal(true)}
            style={{
              flex: 1,
              minWidth: "200px",
              background: "white",
              padding: "16px",
              borderRadius: "14px",
              border: "1px solid #f0f0f0",
              boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
              display: "flex",
              alignItems: "flex-start",
              gap: "16px",
              cursor: "pointer",
              transition: "all 0.2s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-4px)";
              e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.15)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "0 1px 4px rgba(0,0,0,0.08)";
            }}
          >
            <div
              style={{
                minWidth: "48px",
                height: "48px",
                borderRadius: "12px",
                background: "linear-gradient(135deg, #3b82f6, #1e40af)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "white",
                boxShadow: "inset 0 0 4px rgba(0,0,0,0.2)",
              }}
            >
              <FiSend size={24} />
            </div>

            <div style={{ textAlign: "left" }}>
              <p style={{ fontSize: "12px", fontWeight: 600, color: "#6b7280" }}>
                Interest Sent
              </p>
              <p
                style={{
                  fontSize: "26px",
                  fontWeight: 800,
                  marginTop: "4px",
                  color: "#111827",
                }}
              >
                {interestData.sentCount}
              </p>
              <p style={{ fontSize: "10px", marginTop: "6px", color: "#9ca3af" }}>
                Click to view details
              </p>
            </div>
          </button>

          {/* Messages */}
          <button
            onClick={() => setShowMessagesModal(true)}
            style={{
              flex: 1,
              minWidth: "200px",
              background: "white",
              padding: "16px",
              borderRadius: "14px",
              border: "1px solid #f0f0f0",
              boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
              display: "flex",
              alignItems: "flex-start",
              gap: "16px",
              cursor: "pointer",
              transition: "all 0.2s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-4px)";
              e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.15)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "0 1px 4px rgba(0,0,0,0.08)";
            }}
          >
            <div
              style={{
                minWidth: "48px",
                height: "48px",
                borderRadius: "12px",
                background: "linear-gradient(135deg, #a855f7, #6b21a8)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "white",
                boxShadow: "inset 0 0 4px rgba(0,0,0,0.2)",
              }}
            >
              <FiMessageSquare size={24} />
            </div>

            <div style={{ textAlign: "left" }}>
              <p style={{ fontSize: "12px", fontWeight: 600, color: "#6b7280" }}>
                Messages
              </p>
              <p
                style={{
                  fontSize: "26px",
                  fontWeight: 800,
                  marginTop: "4px",
                  color: "#111827",
                }}
              >
                {messageData.count}
              </p>
              <p style={{ fontSize: "10px", marginTop: "6px", color: "#9ca3af" }}>
                Click to view details
              </p>
            </div>
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-md border mb-6">
        <div className="border-b">
          <nav
            style={{
              display: "flex",
              justifyContent: "space-evenly",
              width: "100%",
              padding: "0 8px",
              borderBottom: "1px solid #e5e7eb",
              background: "#fff",
              cursor: "pointer"
            }}
          >
            {[
              { id: "basic", label: "Basic Information" },
              { id: "astrology", label: "Astrology Details" },
              { id: "preferences", label: "Partner Preferences" },
              { id: "zodiac", label: "Zodiac Details" },
              { id: "image", label: "Image Details" },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`relative py-3 px-5 text-sm font-medium transition-all duration-200 cursor-pointer
           ${activeTab === tab.id
                    ? "text-blue-600"
                    : "text-gray-500 hover:text-gray-700"}`}
                style={{
                  background: "transparent",
                  border: "none",
                  position: "relative",
                }}
              >
                {tab.label}

                {/* Blue underline on active tab */}
                {activeTab === tab.id && (
                  <span
                    style={{
                      position: "absolute",
                      bottom: "-1px",
                      left: "0",
                      right: "0",
                      margin: "auto",
                      height: "3px",
                      background: "#2563eb",
                      borderRadius: "10px",
                    }}
                  ></span>
                )}
              </button>
            ))}
          </nav>

        </div>

        <div className="user-details-card p-4">
          {activeTab === 'basic' && <BasicInfoTab />}
          {activeTab === 'astrology' && <AstrologyDetailsTab />}
          {activeTab === 'preferences' && <PartnerPreferencesTab />}
          {activeTab === 'zodiac' && <ZodiacDetailsTab getZodiacValue={getZodiacValue} />}
          {activeTab === 'image' && <PhotosTab />}
        </div>
      </div>



      {/* Drawer Modals */}
      {showInterestReceivedModal && (
        <InterestReceivedDrawer
          onClose={() => setShowInterestReceivedModal(false)}
          interestData={interestData}
        />
      )}
      {showInterestSentModal && (
        <InterestSentDrawer
          onClose={() => setShowInterestSentModal(false)}
          interestData={interestData}
        />
      )}
      {showMessagesModal && (
        <MessagesDrawer
          onClose={() => setShowMessagesModal(false)}
          messageData={messageData}
        />
      )}
    </div>
  );
}

export default UserProfile;